import { Capacitor } from "@capacitor/core";

const toPos = (pos) => ({ lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy });
const mkErr = (message, code) => Object.assign(new Error(message), { code });

export const isLocationDenied = (e) =>
  e?.code === 1 || /denied|permission|not enabled|disabled|restricted|GLOC-000[378]/i.test(`${e?.code || ""} ${e?.message || ""}`);

// First fix from a location STREAM. CoreLocation's startUpdatingLocation() delivers a
// (coarse) fix almost immediately and then refines — unlike requestLocation(), which
// waits for the desired accuracy and fails on iOS when GPS is slow (indoors, cold start).
function firstStreamFix(Geolocation, { enableHighAccuracy, timeout }) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let watchId = null;
    const stop = () => { if (watchId) Geolocation.clearWatch({ id: watchId }).catch(() => {}); };
    const finish = (fn, v) => { if (settled) return; settled = true; clearTimeout(timer); stop(); fn(v); };
    const timer = setTimeout(() => finish(reject, mkErr("timeout", 3)), timeout);
    Geolocation.watchPosition({ enableHighAccuracy, timeout, maximumAge: 0 }, (pos, err) => {
      if (err) finish(reject, err);
      else if (pos) finish(resolve, toPos(pos));
    }).then((id) => { watchId = id; if (settled) stop(); }, (e) => finish(reject, e));
  });
}

async function nativePosition({ timeout, enableHighAccuracy }) {
  const { Geolocation } = await import("@capacitor/geolocation");
  let perm = await Geolocation.checkPermissions();
  if (perm.location !== "granted" && perm.coarseLocation !== "granted") {
    perm = await Geolocation.requestPermissions({ permissions: ["location", "coarseLocation"] });
  }
  if (perm.location !== "granted" && perm.coarseLocation !== "granted") throw mkErr("permission-denied", 1);
  // 1) Coarse single fix: near-instant (Wi-Fi/cell), plenty for a km-radius search.
  //    A "Best accuracy" requestLocation() on iOS often needs >10s or errors out.
  try {
    return toPos(await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: Math.max(timeout, 10000), maximumAge: 120000 }));
  } catch (e) {
    if (isLocationDenied(e)) throw e;
  }
  // 2) Stream fallback: the first position received wins.
  return firstStreamFix(Geolocation, { enableHighAccuracy, timeout: Math.max(timeout, 15000) });
}

const browserPosition = (opts) => new Promise((resolve, reject) => {
  navigator.geolocation.getCurrentPosition((pos) => resolve(toPos(pos)), reject, opts);
});

// Resolve the user's REAL position (GPS). On the native app we use the Capacitor
// Geolocation plugin (runtime permission prompt); on web/PWA the browser API. We
// NEVER guess from the profile city — the search is centered on the device.
export async function getCurrentPosition({ timeout = 9000, enableHighAccuracy = true } = {}) {
  if (Capacitor?.isNativePlatform?.()) return nativePosition({ timeout, enableHighAccuracy });
  if (!("geolocation" in navigator)) throw mkErr("unsupported", 2);
  try {
    return await browserPosition({ enableHighAccuracy, timeout, maximumAge: 60000 });
  } catch (e) {
    if (isLocationDenied(e) || !enableHighAccuracy) throw e;
    // GPS fix too slow/unavailable → retry with a coarse (network) fix.
    return browserPosition({ enableHighAccuracy: false, timeout: Math.max(timeout, 10000), maximumAge: 300000 });
  }
}
